import { check, integer, sqliteTable, text } from "drizzle-orm/sqlite-core";
import { sql, type InferSelectModel } from "drizzle-orm";
import { auditSchema } from "../../schema/audit";
import { isAdminOrEditor } from "../../config-helpers";
import type { ApiConfig } from "../../routes";
import { inArrayOrNullIfNullable } from "../inArrayOrNullIfNullable";

export const tableName = "projects";
export const name = "Projects";
export const route = "projects";
export const icon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" /></svg>`;

export enum ProjectType {
	WebBasedEditor = "Web-Based Editor",
	WebBasedUtility = "Web-Based Utility",
	Game = "Game",
	VideoGameMod = "Video Game Mod",
	Library = "Library",
}
const projectTypeValues = Object.values(ProjectType);


export enum ProjectStatus {
	Active = "Active",
	Completed = "Completed",
	InLimbo = "In Limbo",
	Abandoned = "Abandoned",
}
const projectStatusValues = Object.values(ProjectStatus);

export enum ProjectURLType {
	LiveWebDeployment = "Live Web Deployment",
	NexusMods = "Nexus Mods",
	NPM = "NPM",
	CodePen = "CodePen"
}
const projectURLTypeValues = Object.values(ProjectURLType);

export const definition = {
	id: text("id").primaryKey(),
	title: text("title").notNull(),
	subtitleText: text("subtitle_text").notNull(),
	projectType: text("project_type").notNull(),
	status: text("status").notNull().default(ProjectStatus.Active),
	descriptionMD: text("description_md").notNull(),
	challengesAndTakeawaysMD: text("challenges_and_takeaways_md").notNull(),
	image: text("image"),
	githubUrl: text("github_url"),
	projectUrl: text("project_url"),
	projectUrlType: text("project_url_type"),
	isCodebaseRepresentative: text("is_codebase_representative").notNull().default("false"),
	startedDate: integer("started_date").notNull(),
	lastMajorProgressDate: integer("last_major_progress_date").notNull(),
	isDraft: text("isDraft").default("true").notNull().$type<boolean>(),
};

export const table = sqliteTable(tableName, {
	...definition,
	...auditSchema,
}, (table) => [
	check("project_type_check", inArrayOrNullIfNullable(table.projectType, projectTypeValues)),
	check("status_check", inArrayOrNullIfNullable(table.status, projectStatusValues)),
	check("project_url_type_check", inArrayOrNullIfNullable(table.projectUrlType, projectURLTypeValues)),
	check("is_codebase_representative_check", inArrayOrNullIfNullable(table.isCodebaseRepresentative, ["true", "false"])),
	check("isDraft_check", inArrayOrNullIfNullable(table.isDraft, ["true", "false"])),
]);

export const access: ApiConfig["access"] = {
	operation: {
		read: true,
		create: isAdminOrEditor,
		update: isAdminOrEditor,
		delete: isAdminOrEditor,
	},
};

export type ProjectRaw = InferSelectModel<typeof table>;

export const fields: ApiConfig["fields"] = {
	id: {
		type: "id",
	},
	title: {
		type: "textField",
	},
	subtitleText: {
		type: "textField",
	},
	projectType: {
		type: "select",
		clientData: {
			valueType: 'string',
			options: projectTypeValues.map((value) => ({
				label: value,
				value,
			})),
		},
	},
	status: {
		type: "select",
		clientData: {
			valueType: 'string',
			options: projectStatusValues.map((value) => ({
				label: value,
				value,
			})),
		},
	},
	descriptionMD: {
		type: "textArea-md",
	},
	challengesAndTakeawaysMD: {
		type: "textArea-md",
	},
	image: {
		type: "image",
	},
	githubUrl: {
		type: "url",
	},
	projectUrl: {
		type: "url",
	},
	projectUrlType: {
		type: "select",
		clientData: {
			valueType: 'string',
			options: projectURLTypeValues.map((value) => ({
				label: value,
				value,
			})),
		},
	},
	isCodebaseRepresentative: {
		type: "boolean",
	},
	startedDate: {
		type: "date",
	},
	lastMajorProgressDate: {
		type: "date",
	},
	isDraft: {
		type: "boolean",
	},
} as const satisfies Record<keyof typeof definition, NonNullable<ApiConfig["fields"]>[string]>;
